import { useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Seo, breadcrumbSchema, organizationSchema } from "../lib/seo"
import {
  Arrow,
  BgImage,
  EyebrowLabel,
  Icon,
  IMG,
  Reveal,
  Section,
  SectionHeader,
  SignalField,
} from "../lib/shared"
import { StepFlow } from "../lib/visuals"
import { SITE } from "../lib/site"

const DETAILS = [
  {
    icon: "mail" as const,
    l: "Email",
    v: SITE.email,
    href: `mailto:${SITE.email}`,
  },
  {
    icon: "phone" as const,
    l: "Phone",
    v: SITE.phone,
    href: `tel:${SITE.phoneHref}`,
  },
  { icon: "pin" as const, l: "Location", v: SITE.locationLabel, href: null },
]

const API_URL = (import.meta.env?.VITE_API_URL ?? "").replace(/\/$/, "")

/* These strings are stored on the lead and mapped to a number by the API
   (BUDGET_VALUE in server/src/routes/public.ts). Change both together. */
const BUDGETS = ["Under ₦1m", "₦1m to ₦5m", "₦5m to ₦15m", "₦15m+"]

const NEXT_STEPS = [
  {
    n: "01",
    t: "We read it and reply",
    d: "A real person reads every enquiry. You'll hear back within two working days, usually with a few questions.",
  },
  {
    n: "02",
    t: "We have a short call",
    d: "We talk through your business, what's getting in the way and what a good result would look like.",
  },
  {
    n: "03",
    t: "You get a written proposal",
    d: "Scope, price and timeline in writing, so you can decide with everything in front of you. No obligation.",
  },
]
const SERVICE_OPTS = [
  "Custom Software",
  "Digital Transformation",
  "Government & Institutional",
  "Maintenance & Support",
  "Software Products",
]

export default function Contact() {
  const [sent, setSent] = useState(false)
  const [service, setService] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (busy) return

    const form = e.currentTarget
    const data = new FormData(form)
    const payload = {
      name: String(data.get("name") ?? "").trim(),
      email: String(data.get("email") ?? "").trim(),
      company: String(data.get("company") ?? "").trim(),
      website: String(data.get("website") ?? "").trim(),
      budget: String(data.get("budget") ?? ""),
      message: String(data.get("message") ?? "").trim(),
      service: service ?? "",
      /* Honeypot — see the hidden field below. */
      fax: String(data.get("fax") ?? ""),
    }

    if (!API_URL) {
      setError(
        "The enquiry form is not connected yet. Please email " +
          SITE.email +
          " and we will pick it up there.",
      )
      return
    }

    setBusy(true)
    setError(null)

    /*
      The two failure modes need different copy, and they are told apart by
      where they happen rather than by matching the browser's message — that
      text is "Failed to fetch" in Chrome, "Load failed" in Safari and
      "NetworkError..." in Firefox, so a string comparison would show two
      thirds of visitors a raw browser error instead of our address.
    */
    let res: Response
    try {
      res = await fetch(`${API_URL}/api/public/contact`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      })
    } catch {
      setError(
        `We could not reach our server. Please email ${SITE.email} instead.`,
      )
      setBusy(false)
      return
    }

    try {
      const body = (await res.json().catch(() => null)) as
        | { error?: string }
        | null

      if (!res.ok) {
        /* A failed send must never look like a successful one — the visitor
           would walk away believing we have their message. */
        throw new Error(
          body?.error ?? "We could not send that. Please try again.",
        )
      }

      form.reset()
      setService(null)
      setSent(true)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Something went wrong. Please email ${SITE.email} instead.`,
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Seo
        title="Start a project"
        description="Tell us about your business and what you need. Sableboxx replies to every enquiry within two working days, from Abuja, working with clients anywhere."
        jsonLd={[
          organizationSchema(),
          breadcrumbSchema([{ name: "Contact", path: "/contact" }]),
        ]}
      />
      <section className="relative isolate overflow-hidden bg-ink text-white">
        <div className="absolute inset-0 -z-10">
          <BgImage src={IMG.contactHero} className="opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/50" />
        </div>
        <SignalField />
        <div className="mx-auto max-w-[1280px] px-6 pb-20 pt-40 lg:px-10 lg:pt-48">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal>
                <EyebrowLabel light>Start a project</EyebrowLabel>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="mt-6 text-[clamp(2.6rem,4.6vw,3.8rem)] font-black leading-[1.02] tracking-[-0.035em]">
                  Tell us about your project
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 max-w-md text-[16px] leading-[1.7] text-neutral-400">
                  Tell us where things stand and where you want to get to.
                  We reply to every enquiry within two working days with clear
                  next steps.
                </p>
              </Reveal>
              <Reveal delay={220}>
                <div className="mt-12 space-y-6">
                  {DETAILS.map((d) => (
                    <div key={d.l} className="flex items-start gap-4">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-white/12 bg-white/5">
                        <Icon name={d.icon} className="h-5 w-5" />
                      </span>
                      <div>
                        <div className="text-[12px] font-medium uppercase tracking-[0.16em] text-neutral-400">
                          {d.l}
                        </div>
                        {d.href ? (
                          <a
                            href={d.href}
                            className="mt-1 block text-[15px] text-white underline-offset-4 transition-colors hover:text-accent-bright hover:underline"
                          >
                            {d.v}
                          </a>
                        ) : (
                          <div className="mt-1 text-[15px] text-white">
                            {d.v}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* Form */}
            <div className="lg:col-span-7">
              <Reveal delay={120}>
                <div className="rounded-3xl bg-white p-8 text-ink lg:p-10">
                  {sent ? (
                    <div className="flex min-h-[420px] flex-col items-start justify-center lg:items-center lg:text-center">
                      <span className="grid h-16 w-16 place-items-center rounded-full bg-accent text-white">
                        <Icon name="credible" className="h-8 w-8" />
                      </span>
                      <h2 className="mt-6 text-[26px] font-black tracking-[-0.02em]">
                        Message received.
                      </h2>
                      <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-neutral-500">
                        Thank you. We&rsquo;ll reply within two working days
                        with clear next steps.
                      </p>
                      <button
                        onClick={() => setSent(false)}
                        className="mt-8 text-[13px] font-medium text-ink underline underline-offset-4"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form ref={formRef} onSubmit={onSubmit} noValidate={false}>
                      {/* Honeypot: hidden from people, filled in by bots. The
                          API silently discards any submission that sets it. */}
                      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
                        <label>
                          Fax
                          <input
                            type="text"
                            name="fax"
                            tabIndex={-1}
                            autoComplete="off"
                          />
                        </label>
                      </div>

                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <Field label="Full name" name="name" placeholder="Your full name" />
                        <Field
                          label="Email"
                          name="email"
                          type="email"
                          placeholder="you@company.com"
                        />
                        <Field
                          label="Company"
                          name="company"
                          placeholder="Company name"
                        />
                        <Field
                          label="Website"
                          name="website"
                          placeholder="yourcompany.com"
                          required={false}
                        />
                      </div>

                      <div className="mt-7">
                        <Label>What do you need?</Label>
                        <div className="mt-3 flex flex-wrap gap-2.5">
                          {SERVICE_OPTS.map((s) => (
                            <button
                              type="button"
                              key={s}
                              onClick={() => setService(s)}
                              className={`rounded-full border px-4 py-2 text-[13px] font-medium transition-colors ${
                                service === s
                                  ? "border-ink bg-ink text-white"
                                  : "border-neutral-300 text-neutral-600 hover:border-ink hover:text-ink"
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mt-7">
                        <Label>Budget</Label>
                        <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                          {BUDGETS.map((b) => (
                            <label
                              key={b}
                              className="cursor-pointer rounded-xl border border-neutral-300 px-3 py-2.5 text-center text-[13px] text-neutral-600 transition-colors has-[:checked]:border-ink has-[:checked]:bg-ink has-[:checked]:text-white"
                            >
                              <input
                                type="radio"
                                name="budget"
                                value={b}
                                className="sr-only"
                              />
                              {b}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="mt-7">
                        <Label>Tell us about the project</Label>
                        <textarea
                          name="message"
                          rows={4}
                          required
                          placeholder="A few sentences on what you're building and where you are today."
                          className="mt-2 w-full resize-none rounded-xl border border-neutral-300 bg-white px-4 py-3 text-[15px] outline-none transition-colors placeholder:text-neutral-400 focus:border-ink"
                        />
                      </div>

                      {error && (
                        <p
                          role="alert"
                          className="mt-7 rounded-xl bg-red-50 px-4 py-3 text-[13.5px] leading-relaxed text-red-700"
                        >
                          {error}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={busy}
                        className="group mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-ink px-6 py-4 text-[14px] font-medium text-white transition-all duration-300 hover:bg-black/85 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                      >
                        {busy ? "Sending…" : "Send message"}
                        {!busy && (
                          <Arrow className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        )}
                      </button>

                      <p className="mt-5 text-[12.5px] leading-relaxed text-neutral-500">
                        By sending this you agree to our{" "}
                        <Link
                          to="/privacy"
                          className="underline underline-offset-2 hover:text-ink"
                        >
                          Privacy Policy
                        </Link>
                        . We use your details to reply to this enquiry and
                        nothing else.
                      </p>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* What happens next */}
      <Section tone="paper">
        <SectionHeader
          eyebrow="What happens next"
          title="From first message to a clear plan"
          intro="No pressure and no jargon. Here's what to expect after you get in touch."
        />
        <StepFlow steps={NEXT_STEPS} />
      </Section>
    </>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[12px] font-medium uppercase tracking-[0.14em] text-neutral-500">
      {children}
    </span>
  )
}

function Field({
  label,
  name,
  type = "text",
  placeholder,
  required = true,
}: {
  label: string
  name: string
  type?: string
  placeholder?: string
  required?: boolean
}) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <input
        type={type}
        name={name}
        required={required}
        placeholder={placeholder}
        className="mt-2 w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-[15px] outline-none transition-colors placeholder:text-neutral-400 focus:border-ink"
      />
    </label>
  )
}
